import { format } from 'date-fns';
import { useMyScholarshipMembers } from '@/hooks/members/useMyScholarshipMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import { sortFnTags } from '@/lib/member-views/sortRows';
import type { ScholarshipMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<ScholarshipMemberRow>;

/**
 * Scholarship Members — the practice's Active and Suspended members on a Scholarship billing
 * schedule, in the server's last-name order until a column is sorted.
 *
 * The report is the 19 columns below, in `csv.order`: legacy's report less its Title column, which
 * this view's API doesn't return. Established and Last AWP are the table's own fields; the table's
 * Patient and Phone combine two values each, so the report has its own name and phone columns.
 */
export const scholarshipMembersDataset: MemberDataset<ScholarshipMemberRow> = {
  key: 'scholarship',
  rowKey: (row) => row.id,
  useRows: useMyScholarshipMembers,
  alphaKey: (row) => row.lastName,
  // The same provisional ceiling as All Active; these members are a subset of it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_Scholarship_Members_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round, with the MDVIP ID.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'established',
      label: 'memberViews.columns.established',
      width: 'sm',
      value: (row) => row.memberSince,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 4, label: 'memberViews.csv.columns.dateEstablished' },
    },
    {
      id: 'lastAwp',
      label: 'memberViews.columns.lastAwp',
      width: 'sm',
      value: (row) => row.lastAwpDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 13, label: 'memberViews.csv.columns.lastAwpDate' },
    },
    {
      id: 'phone',
      label: 'memberViews.columns.phone',
      width: 'lg',
      value: (row) => row.mobilePhone ?? row.phone,
      format: 'phone',
      table: true,
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      // No tags is a missing value, so untagged rows sort last both ways, as the design asks.
      value: (row) => (row.priorityTags.length > 0 ? row.priorityTags : null),
      table: true,
      sortable: true,
      sortFn: sortFnTags,
    },
    reportOnly('lastName', 1, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('firstName', 2, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('mdvipId', 3, 'memberViews.csv.columns.mdvipId', (row) => row.mdvipId),
    reportOnly('renewalDate', 5, 'memberViews.csv.columns.renewalDate', (row) => row.renewalDate, 'date'),
    reportOnly('mailingStreet', 6, 'memberViews.csv.columns.mailingStreet', (row) => row.mailingStreet),
    reportOnly('city', 7, 'memberViews.csv.columns.city', (row) => row.mailingCity),
    reportOnly('state', 8, 'memberViews.csv.columns.state', (row) => row.mailingState),
    reportOnly('zip', 9, 'memberViews.csv.columns.zip', (row) => row.mailingZip),
    // The phone numbers as the API returns them, as legacy's report printed them.
    reportOnly('homePhone', 10, 'memberViews.csv.columns.phone', (row) => row.phone),
    reportOnly('cellPhone', 11, 'memberViews.csv.columns.cellPhone', (row) => row.mobilePhone),
    reportOnly('email', 12, 'memberViews.csv.columns.email', (row) => row.email),
    reportOnly('status', 14, 'memberViews.csv.columns.status', (row) => row.memberStatus),
    reportOnly('dob', 15, 'memberViews.csv.columns.dob', (row) => row.dateOfBirth, 'date'),
    reportOnly('birthday', 16, 'memberViews.csv.columns.birthday', (row) => row.nextBirthday, 'date'),
    reportOnly('insurance', 17, 'memberViews.csv.columns.insurance', (row) => row.insuranceName),
    reportOnly(
      'paymentSchedule',
      18,
      'memberViews.csv.columns.paymentSchedule',
      (row) => row.currentAgreementPaymentSchedule,
    ),
    reportOnly(
      'scholarshipEndDate',
      19,
      'memberViews.csv.columns.scholarshipEndDate',
      (row) => row.scholarshipEndDate,
      'date',
    ),
  ],
};
